/**
 * WriterPortal — self-contained component for blog writers.
 * Shows the writer's own posts, lets them create/edit drafts,
 * and submit for admin review. Embeds in account & creator dashboard.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PenLine,
  ArrowLeft,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Send,
  Trash2,
  ExternalLink,
} from 'lucide-react';

import { apiRequest, fetchJson } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { BlogEditor } from '@/components/blog-editor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface WriterPost {
  id: string;
  title: string;
  slug: string;
  status: string;
  excerpt: string | null;
  featuredImageUrl: string | null;
  regionId: string | null;
  localityId: string | null;
  regionSlug: string | null;
  localitySlug: string | null;
  rejectionNote: string | null;
  publishedAt: string | null;
  scheduledFor: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Region {
  id: string;
  name: string;
  slug: string;
  type: string;
}

interface Locality {
  id: string;
  name: string;
  slug: string;
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    icon: React.ReactNode;
  }
> = {
  draft: { label: 'Draft', variant: 'secondary', icon: <PenLine className="h-3 w-3" /> },
  pending_review: { label: 'In Review', variant: 'default', icon: <Clock className="h-3 w-3" /> },
  changes_requested: {
    label: 'Changes Requested',
    variant: 'destructive',
    icon: <AlertCircle className="h-3 w-3" />,
  },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
  scheduled: { label: 'Scheduled', variant: 'outline', icon: <Clock className="h-3 w-3" /> },
  published: { label: 'Published', variant: 'default', icon: <CheckCircle2 className="h-3 w-3" /> },
  archived: { label: 'Archived', variant: 'secondary', icon: <Eye className="h-3 w-3" /> },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, variant: 'secondary' as const, icon: null };
  return (
    <Badge variant={cfg.variant} className="flex items-center gap-1 w-fit">
      {cfg.icon}
      {cfg.label}
    </Badge>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface PostFormState {
  title: string;
  excerpt: string;
  content: string;
  featuredImageUrl: string;
  categoryId: string;
  tags: string;
  regionId: string;
  localitySlug: string;
  seoTitle: string;
  seoDescription: string;
}

const EMPTY_FORM: PostFormState = {
  title: '',
  excerpt: '',
  content: '',
  featuredImageUrl: '',
  categoryId: '',
  tags: '',
  regionId: '',
  localitySlug: '',
  seoTitle: '',
  seoDescription: '',
};

export function WriterPortal() {
  const [view, setView] = useState<'list' | 'edit'>('list');
  const [editingPost, setEditingPost] = useState<WriterPost | null>(null);
  const [form, setForm] = useState<PostFormState>(EMPTY_FORM);
  const [localityId, setLocalityId] = useState<string>('');

  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: posts = [], isLoading: postsLoading } = useQuery<WriterPost[]>({
    queryKey: ['/api/writer/posts'],
    queryFn: () => fetchJson<WriterPost[]>('/api/writer/posts'),
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ['/api/regions'],
    queryFn: () => fetchJson<Region[]>('/api/regions'),
  });

  const { data: localities = [] } = useQuery<Locality[]>({
    queryKey: ['/api/regions', form.regionId, 'localities'],
    queryFn: async () => {
      const region = regions.find(r => r.id === form.regionId);
      if (!region) return [];
      return fetchJson<Locality[]>(`/api/regions/${region.slug}/localities`);
    },
    enabled: !!form.regionId,
  });

  const { data: categories = [] } = useQuery<BlogCategory[]>({
    queryKey: ['/api/blog/categories'],
    queryFn: () => fetchJson<BlogCategory[]>('/api/blog/categories'),
  });

  // When the region changes, clear the locality
  useEffect(() => {
    setLocalityId('');
  }, [form.regionId]);

  // When locality list loads, map localitySlug → id
  useEffect(() => {
    if (form.localitySlug && localities.length > 0) {
      const found = localities.find(l => l.slug === form.localitySlug);
      if (found) setLocalityId(found.id);
    }
  }, [form.localitySlug, localities]);

  const createMutation = useMutation({
    mutationFn: (data: object) => apiRequest('POST', '/api/admin/blog', data).then(r => r.json()),
    onSuccess: post => {
      qc.invalidateQueries({ queryKey: ['/api/writer/posts'] });
      setEditingPost(post);
      toast({ title: 'Draft saved' });
    },
    onError: () => toast({ title: 'Failed to save', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiRequest('PATCH', `/api/admin/blog/${id}`, data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/api/writer/posts'] });
      toast({ title: 'Draft saved' });
    },
    onError: () => toast({ title: 'Failed to save', variant: 'destructive' }),
  });

  const submitMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest('POST', `/api/admin/blog/${id}/submit`).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/api/writer/posts'] });
      setView('list');
      setEditingPost(null);
      toast({ title: 'Post submitted for review' });
    },
    onError: () => toast({ title: 'Failed to submit', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/admin/blog/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/api/writer/posts'] });
      toast({ title: 'Post deleted' });
    },
    onError: () => toast({ title: 'Failed to delete', variant: 'destructive' }),
  });

  function openNew() {
    setEditingPost(null);
    setForm(EMPTY_FORM);
    setLocalityId('');
    setView('edit');
  }

  function openEdit(post: WriterPost) {
    setEditingPost(post);
    const region = regions.find(r => r.id === post.regionId);
    setForm({
      title: post.title ?? '',
      excerpt: post.excerpt ?? '',
      content: '',
      featuredImageUrl: post.featuredImageUrl ?? '',
      categoryId: '',
      tags: '',
      regionId: post.regionId ?? '',
      localitySlug: '',
      seoTitle: '',
      seoDescription: '',
    });
    setLocalityId(post.localityId ?? '');
    // Fetch full post for content
    fetch(`/api/admin/blog/${post.id}`)
      .then(r => r.json())
      .then(full => {
        setForm(prev => ({
          ...prev,
          content: full.content ?? '',
          categoryId: full.categoryId ?? '',
          tags: Array.isArray(full.tags) ? full.tags.join(', ') : '',
          seoTitle: full.seoTitle ?? '',
          seoDescription: full.seoDescription ?? '',
          regionId: full.regionId ?? region?.id ?? '',
          localitySlug: '',
        }));
        setLocalityId(full.localityId ?? '');
      });
    setView('edit');
  }

  function buildPayload() {
    const region = regions.find(r => r.id === form.regionId);
    return {
      title: form.title,
      excerpt: form.excerpt || null,
      content: form.content,
      featuredImageUrl: form.featuredImageUrl || null,
      categoryId: form.categoryId || null,
      tags: form.tags
        ? form.tags
            .split(',')
            .map(t => t.trim())
            .filter(Boolean)
        : [],
      regionId: region?.id || null,
      localityId: localityId || null,
      seoTitle: form.seoTitle || null,
      seoDescription: form.seoDescription || null,
    };
  }

  async function handleSave() {
    if (!form.title.trim() || !form.content.trim()) {
      toast({ title: 'Title and content are required', variant: 'destructive' });
      return;
    }
    if (editingPost) {
      const updated = await updateMutation.mutateAsync({
        id: editingPost.id,
        data: buildPayload(),
      });
      setEditingPost(updated);
    } else {
      const created = await createMutation.mutateAsync(buildPayload());
      setEditingPost(created);
    }
  }

  async function handleSubmit() {
    if (!editingPost) return;
    if (!form.regionId) {
      toast({ title: 'Please select a region before submitting', variant: 'destructive' });
      return;
    }
    // Save first, then submit
    if (form.title.trim() && form.content.trim()) {
      await updateMutation.mutateAsync({ id: editingPost.id, data: buildPayload() });
    }
    submitMutation.mutate(editingPost.id);
  }

  const canEdit = (post: WriterPost) =>
    ['draft', 'changes_requested', 'rejected'].includes(post.status);
  const canSubmit = (post: WriterPost) =>
    ['draft', 'changes_requested', 'rejected'].includes(post.status);
  const canDelete = (post: WriterPost) =>
    ['draft', 'rejected', 'changes_requested'].includes(post.status);

  const isSubmitting =
    createMutation.isPending || updateMutation.isPending || submitMutation.isPending;

  // ── Edit view ───────────────────────────────────────────────────────────────
  if (view === 'edit') {
    const physicalRegions = regions.filter(r => r.type === 'physical');
    const canSubmitPost = editingPost && canSubmit(editingPost);

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setView('list');
              setEditingPost(null);
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h2 className="text-lg font-semibold">{editingPost ? 'Edit post' : 'New post'}</h2>
          {editingPost && <StatusBadge status={editingPost.status} />}
        </div>

        {/* Review note alert */}
        {editingPost?.rejectionNote &&
          ['changes_requested', 'rejected'].includes(editingPost.status) && (
            <div className="flex gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-destructive">
                  {editingPost.status === 'changes_requested'
                    ? 'Changes requested by admin'
                    : 'Post not approved'}
                </p>
                <p className="text-sm mt-1">{editingPost.rejectionNote}</p>
              </div>
            </div>
          )}

        <div className="grid gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="writer-title">Title *</Label>
            <Input
              id="writer-title"
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Post title"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="writer-excerpt">Excerpt (optional)</Label>
            <Textarea
              id="writer-excerpt"
              value={form.excerpt}
              onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))}
              placeholder="Short summary shown in the post grid"
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Content *</Label>
            <BlogEditor
              value={form.content}
              onChange={html => setForm(p => ({ ...p, content: html }))}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Region *</Label>
              <Select
                value={form.regionId}
                onValueChange={v => setForm(p => ({ ...p, regionId: v, localitySlug: '' }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {physicalRegions.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Locality (optional)</Label>
              <Select
                value={localityId}
                onValueChange={setLocalityId}
                disabled={!form.regionId || localities.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={form.regionId ? 'Select locality' : 'Select region first'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All localities</SelectItem>
                  {localities.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Category (optional)</Label>
              <Select
                value={form.categoryId}
                onValueChange={v => setForm(p => ({ ...p, categoryId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No category</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="writer-image">Featured image URL (optional)</Label>
              <Input
                id="writer-image"
                value={form.featuredImageUrl}
                onChange={e => setForm(p => ({ ...p, featuredImageUrl: e.target.value }))}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="writer-tags">Tags (optional, comma-separated)</Label>
            <Input
              id="writer-tags"
              value={form.tags}
              onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
              placeholder="news, events, community"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <Button onClick={handleSave} disabled={isSubmitting} variant="outline">
            Save draft
          </Button>
          {canSubmitPost && (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              <Send className="h-4 w-4 mr-1.5" />
              {editingPost.status === 'changes_requested'
                ? 'Resubmit for review'
                : 'Submit for review'}
            </Button>
          )}
          {editingPost && canSubmitPost && (
            <p className="text-xs text-muted-foreground ml-auto">
              Save first, then submit — or click Submit to do both.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">My Posts</h2>
          <p className="text-sm text-muted-foreground">
            Write and submit posts for admin review before they go live.
          </p>
        </div>
        <Button onClick={openNew}>
          <PenLine className="h-4 w-4 mr-1.5" />
          New post
        </Button>
      </div>

      {postsLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground space-y-2">
            <PenLine className="h-8 w-8 mx-auto opacity-30" />
            <p className="font-medium">No posts yet</p>
            <p className="text-sm">Create your first post and submit it for review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <Card key={post.id} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <StatusBadge status={post.status} />
                      <span className="text-xs text-muted-foreground">
                        Updated {formatDate(post.updatedAt)}
                      </span>
                    </div>
                    <p className="font-medium truncate">{post.title}</p>
                    {post.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Review note */}
                    {post.rejectionNote &&
                      ['changes_requested', 'rejected'].includes(post.status) && (
                        <div className="mt-2 flex gap-2 p-2.5 rounded bg-destructive/10 border border-destructive/20">
                          <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                          <p className="text-xs text-destructive">{post.rejectionNote}</p>
                        </div>
                      )}

                    {post.scheduledFor && post.status === 'scheduled' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Scheduled for {formatDate(post.scheduledFor)}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {canEdit(post) && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(post)}>
                        <PenLine className="h-3.5 w-3.5 mr-1" />
                        Edit
                      </Button>
                    )}
                    {post.status === 'published' && post.regionSlug && (
                      <a
                        href={
                          post.localitySlug
                            ? `/regions/${post.regionSlug}/${post.localitySlug}/updates/${post.slug}`
                            : `/regions/${post.regionSlug}/updates/${post.slug}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="View live post"
                      >
                        <Button size="sm" variant="ghost" title="View live post">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </a>
                    )}
                    {post.status === 'pending_review' && (
                      <span className="text-xs text-muted-foreground px-2">Awaiting review</span>
                    )}
                    {canDelete(post) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm('Delete this draft?')) deleteMutation.mutate(post.id);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
